'use client';

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { fishingCatchTier, fishingCatchVariant } from '@/lib/fishing-catch';
import { FishingDayPhase } from '@/lib/fishing-day-phase';
import { AmbientWorldEventKind } from '@/lib/fishing-world-catalog';
import {
  createFishingWorldRenderDecision,
  type FishingWorldRenderDecision,
} from '@/lib/fishing-world-render-contract';
import styles from './FishingScene.module.css';
import { WaterShader } from './WaterShader';
import { AmbientWildlife } from './AmbientWildlife';
import { AmbientFish } from './AmbientFish';
import { AmbientNessie } from './AmbientNessie';
import { AmbientMicroLife } from './AmbientMicroLife';

export type FishingSceneState = 'idle' | 'catch' | 'release';

export interface FishingSceneProps {
  state?: FishingSceneState;
  amount?: number | string;
  catchEventId?: string;
  debugCatchFrame?: number | null;
  catchCount?: number;
  dayPhase?: FishingDayPhase;
  worldDecision?: FishingWorldRenderDecision;
  onAmbientEventComplete?: (eventId: string) => void;
  onBadgerVisitChange?: (side: import('./AmbientWildlife').BadgerSide | null) => void;
  className?: string;
  'data-testid'?: string;
}

const formatAmount = (amount: FishingSceneProps['amount']) => {
  if (typeof amount === 'number') return new Intl.NumberFormat('ru-RU').format(amount);
  return amount ?? '0';
};

export function FishingScene({
  state = 'idle',
  amount,
  catchEventId,
  debugCatchFrame = null,
  catchCount = 0,
  dayPhase = FishingDayPhase.Day,
  worldDecision,
  onAmbientEventComplete,
  onBadgerVisitChange,
  className,
  'data-testid': testId = 'fishing-scene',
}: FishingSceneProps) {
  const [isRecasting, setIsRecasting] = useState(false);
  const recastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isCatch = state === 'catch';
  const isRelease = state === 'release';
  const isActiveEvent = isCatch || isRelease;

  useEffect(() => () => {
    if (recastTimer.current) clearTimeout(recastTimer.current);
  }, []);

  useEffect(() => {
    if (!isActiveEvent || !isRecasting) return;
    if (recastTimer.current) clearTimeout(recastTimer.current);
    recastTimer.current = null;
    setIsRecasting(false);
  }, [isActiveEvent, isRecasting]);

  const recast = () => {
    if (isActiveEvent || isRecasting) return;
    setIsRecasting(true);
    recastTimer.current = setTimeout(() => {
      recastTimer.current = null;
      setIsRecasting(false);
    }, 1_650);
  };
  const catchVariant = isActiveEvent && typeof amount === 'number'
    ? fishingCatchVariant(amount, catchEventId ?? String(amount))
    : 0;
  const catchTier = isActiveEvent && typeof amount === 'number' ? fishingCatchTier(amount) : undefined;
  const ambientWorld = createFishingWorldRenderDecision(
    worldDecision?.activeEvents ?? [],
  );
  const ambientEvent = (kind: AmbientWorldEventKind) => ambientWorld.activeEvents.find((event) => event.kind === kind);
  const label = isCatch
    ? `Улов пойман: ${formatAmount(amount)} ₽`
    : isRelease
      ? `Рыба отпущена после отмены заказа: ${formatAmount(amount)} ₽`
      : 'Рыбак на вечернем озере ждёт поклёвку';

  return (
    <section
      className={[styles.scene, isCatch && styles.catch, isRelease && styles.release, isRecasting && styles.recast, className].filter(Boolean).join(' ')}
      aria-label={label}
      data-day-phase={dayPhase}
      data-testid={testId}
    >
      <div className={styles.lakeBackdrop} role="img" aria-label="Вечернее озеро с деревянным причалом" />
      <WaterShader dayPhase={dayPhase} />
      <AmbientWildlife
        birdFlightsEvent={ambientEvent(AmbientWorldEventKind.Birds)}
        murmurationEvent={ambientEvent(AmbientWorldEventKind.DistantSparrowFlock)}
        badgerEvent={ambientEvent(AmbientWorldEventKind.Badger)}
        onComplete={onAmbientEventComplete}
        onBadgerVisitChange={onBadgerVisitChange}
      />
      <AmbientFish event={ambientEvent(AmbientWorldEventKind.FishJump)} onComplete={onAmbientEventComplete} />
      <AmbientNessie event={ambientEvent(AmbientWorldEventKind.Nessie)} onComplete={onAmbientEventComplete} />
      <AmbientMicroLife events={ambientWorld.activeEvents} onComplete={onAmbientEventComplete} />
      <div className={styles.waterRippleLayer} aria-hidden="true">
        <div className={`${styles.waterRipple} ${styles.ripplePost}`}><i /><i /><i /></div>
        <div className={`${styles.waterRipple} ${styles.rippleRocks}`}><i /><i /><i /></div>
        <div className={`${styles.waterRipple} ${styles.rippleBobber}`}><i /><i /><i /></div>
      </div>
      <div className={styles.bobber} aria-hidden="true" />
      <div className={styles.jewelryGlints} aria-hidden="true" />
      <div
        className={styles.fishermanSprite}
        data-debug-frame={debugCatchFrame === null ? undefined : debugCatchFrame}
        style={debugCatchFrame === null ? undefined : { backgroundPositionX: `${(debugCatchFrame / 23) * 100}%` }}
        data-testid="fishing-fisherman-sprite"
        role="presentation"
      />
      {!isActiveEvent && (
        <button
          type="button"
          className={styles.recastAction}
          onClick={recast}
          onPointerMove={(event) => {
            const bounds = event.currentTarget.getBoundingClientRect();
            event.currentTarget.style.setProperty('--recast-pointer-x', `${event.clientX - bounds.left}px`);
            event.currentTarget.style.setProperty('--recast-pointer-y', `${event.clientY - bounds.top}px`);
          }}
          disabled={isRecasting}
          aria-label="Перезабросить удочку"
          title="Перезабросить удочку"
        >
          <span aria-hidden="true">↻</span>
          <strong>Перезабросить</strong>
        </button>
      )}
      {isActiveEvent && (
        <i
          className={styles.caughtFishSprite}
          style={{
            '--fish-column': catchVariant % 5,
            '--fish-row': Math.floor(catchVariant / 5),
          } as CSSProperties}
          data-tier={catchTier}
          aria-hidden="true"
        />
      )}
      <div
        className={styles.bucketSprite}
        data-fill={catchCount === 0 ? '0' : catchCount === 1 ? '1' : catchCount < 5 ? '2' : '3'}
        aria-label={`Уловов в ведре: ${catchCount}`}
      />
      <div
        className={styles.siameseCat}
        role="img"
        aria-label="Сиамская кошка сидит на причале"
        data-testid="fishing-siamese-cat"
      />
      {isActiveEvent && <div className={styles.catchSplash} aria-hidden="true"><i /><i /><i /></div>}

      {isActiveEvent && (
        <div className={styles.badge} aria-live="polite" data-testid="fishing-catch-badge">
          <span>{isRelease ? 'Отпустили' : 'Улов'}</span>
          <strong>{isRelease ? '−' : '+'}{formatAmount(amount)} ₽</strong>
        </div>
      )}
    </section>
  );
}

export default FishingScene;
